<template>
    <div id="gpsInfoModal" class="modal fade" tabindex="-1" style="z-index: 1075;">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">How GPS tracking &amp; odometer data works</h5>
                </div>
                <div class="modal-body" style="font-size: 0.9rem; line-height: 1.7;">

                    <h6 class="fw-bold border-bottom pb-1 mb-2">📍 How we collect location data</h6>
                    <p>While a journey is active the Trips driver app continuously tracks the vehicle's position in the background using a GPS tracking library. The app records a new location point whenever the vehicle moves more than 300 metres, or every 60 seconds on a time-based fallback — whichever comes first. If the vehicle is stationary, a check-in is still sent every 5 minutes to confirm no movement has occurred.</p>
                    <p class="mb-3">All collected points are saved on the device first, then uploaded to the server in small batches. If the driver's phone loses mobile data, the points queue up and upload automatically the moment connectivity is restored.</p>

                    <h6 class="fw-bold border-bottom pb-1 mb-2">📐 How GPS distance is calculated</h6>
                    <p>GPS distance is calculated by measuring the straight-line distance between each consecutive pair of uploaded location points (using the haversine formula, which accounts for the curvature of the Earth) and adding them all together.</p>
                    <p class="mb-3">The measurement window for a booking starts at the recorded pickup time and ends 5 minutes after the dropoff time. This buffer ensures that any location points that arrived at the server shortly after the dropoff event are still included in the calculation.</p>

                    <h6 class="fw-bold border-bottom pb-1 mb-2">🛞 How the odometer works</h6>
                    <p>The GPS tracking library maintains its own internal distance counter, separate from location point uploads. When a driver enters their vehicle odometer reading at the start of a journey (for example, 103,800 km), the library's counter is set to that exact value and begins accumulating distance from there as the vehicle moves.</p>
                    <p class="mb-3">At the moment of each pickup and dropoff event, the app reads the current value from this counter and sends it to the server alongside the event — regardless of whether the GPS upload queue has caught up.</p>

                    <h6 class="fw-bold border-bottom pb-1 mb-2">🧮 How booking distances are calculated</h6>
                    <p>When a pickup or dropoff event is recorded the system calculates the booking distance using both GPS and odometer:</p>
                    <ul class="mb-3">
                        <li><strong>If GPS has data</strong> (distance greater than zero) — GPS is used as the primary source.</li>
                        <li><strong>If GPS returns zero</strong> (no points uploaded yet — common when the device is offline) — the odometer reading from the driver app is used instead as a fallback.</li>
                    </ul>

                    <h6 class="fw-bold border-bottom pb-1 mb-2">⚠️ What can cause inaccuracies or low GPS match</h6>
                    <ul class="mb-0">
                        <li><strong>Device offline during the journey</strong> — GPS points queue up on the device and upload when connectivity returns. If the journey ends before they upload, distances are recorded based on what was available. The GPS match % will be low, and uploaded points may show a late upload warning when clicked.</li>
                        <li><strong>GPS signal loss</strong> (tunnels, underground car parks, rural areas) — when satellite signal is weak, accuracy drops and segments appear as gaps in the gap analysis table.</li>
                        <li><strong>GPS noise in dense urban areas</strong> — tall buildings can cause GPS coordinates to "bounce", occasionally showing a match percentage above 100%.</li>
                        <li><strong>Odometer starting at zero</strong> — if a driver did not enter their vehicle odometer at journey start, all odometer-based values reflect only distance since journey start rather than the vehicle's real reading.</li>
                    </ul>

                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary btn-sm" @click="hide()">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Modal } from "bootstrap";
import { onMounted, ref } from "vue";

const modal = ref<Modal | null>(null);

const show = () => modal.value?.show();
const hide = () => modal.value?.hide();

defineExpose({ show, hide });

onMounted(() => {
    const el = document.getElementById('gpsInfoModal');
    if (!el) return;
    modal.value = new Modal(el);
    el.addEventListener('show.bs.modal', () => {
        setTimeout(() => {
            const backdrops = document.querySelectorAll<HTMLElement>('.modal-backdrop');
            if (backdrops.length > 0) backdrops[backdrops.length - 1].style.zIndex = '1070';
        }, 0);
    });
});
</script>
